import {
  CONNECTOR_KINDS,
  type ConnectorKind,
  type ConnectorView,
  EXCHANGE_ENTITIES,
  type ExchangeEntity,
  type FieldMapRow,
  type QueueItemView,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { Button } from '../../ui/button';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Select } from '../../ui/form';
import { Panel } from '../../ui/layout';

const KEY = ['integrations', 'connectors'];
const QKEY = ['integrations', 'queue'];

const TRANSFORMS: Record<FieldMapRow['transform'], string> = {
  as_is: 'как есть',
  date_dmy: 'дата ДД.ММ.ГГГГ',
  decimal_comma: 'сумма с запятой',
  constant: 'константа',
};

type Row = Omit<FieldMapRow, 'constantValue'> & { constantValue: string | null };

/** Выгрузка файлом: скачивается через fetch, потому что это POST с заголовком защиты (CSRF) */
async function downloadCsv(connectorId: string, entity: string): Promise<void> {
  const res = await fetch(`/api/v1/integrations/connectors/${connectorId}/export?entity=${entity}`, {
    method: 'POST',
    headers: { 'x-epc-csrf': '1' },
    credentials: 'same-origin',
  });
  if (!res.ok) {
    const e = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    throw new ApiError(res.status, 'export', e?.error?.message ?? `Ошибка ${res.status}`);
  }
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = `exchange-${entity}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function MapEditor({ c }: { c: ConnectorView }) {
  const [entity, setEntity] = useState<ExchangeEntity | ''>(c.entities[0] ?? '');
  const [rows, setRows] = useState<Row[]>([]);
  const map = useQuery({
    queryKey: ['integrations', 'map', c.id, entity],
    enabled: Boolean(entity),
    queryFn: () => api<{ rows: Row[] }>(`/integrations/connectors/${c.id}/maps/${entity}`),
  });
  useEffect(() => {
    if (map.data) setRows(map.data.rows);
  }, [map.data]);
  const save = useMutation({
    mutationFn: () =>
      api(`/integrations/connectors/${c.id}/maps/${entity}`, { method: 'PUT', body: { rows } }),
    onSuccess: () => toast('Сопоставление сохранено'),
  });
  const set = (i: number, p: Partial<Row>) => setRows((r) => r.map((x, j) => (j === i ? { ...x, ...p } : x)));
  if (c.entities.length === 0) return null;
  return (
    <div className="flex flex-col gap-3">
      <Field label="Какие записи сопоставляем" htmlFor={`ent-${c.id}`}>
        <Select
          className="h-11 md:h-10"
          id={`ent-${c.id}`}
          value={entity}
          onChange={(e) => setEntity(e.target.value as ExchangeEntity)}
        >
          {c.entities.map((e) => (
            <option key={e} value={e}>
              {EXCHANGE_ENTITIES[e]}
            </option>
          ))}
        </Select>
      </Field>
      <p className="text-[13px] text-ink-2">
        Слева — поле записи портала (например, number или counterparty.bin), справа — название поля или
        столбца во внешней системе. Формат обмена задаёте вы: портал ничего не подставляет сам.
      </p>
      {map.isPending && entity ? <Spinner /> : null}
      <ul className="flex flex-col gap-3">
        {rows.map((r, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: строки редактора без собственных id
          <li key={i} className="flex flex-col gap-2 rounded-md border border-line p-3">
            <Input
              className="h-11 md:h-10"
              aria-label="Поле портала"
              placeholder="Поле портала"
              value={r.portalField}
              onChange={(e) => set(i, { portalField: e.target.value })}
            />
            <Input
              className="h-11 md:h-10"
              aria-label="Поле внешней системы"
              placeholder="Поле внешней системы"
              value={r.externalField}
              onChange={(e) => set(i, { externalField: e.target.value })}
            />
            <Select
              className="h-11 md:h-10"
              aria-label="Правило записи"
              value={r.transform}
              onChange={(e) => set(i, { transform: e.target.value as Row['transform'] })}
            >
              {Object.entries(TRANSFORMS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </Select>
            {r.transform === 'constant' ? (
              <Input
                className="h-11 md:h-10"
                aria-label="Значение константы"
                placeholder="Значение"
                value={r.constantValue ?? ''}
                onChange={(e) => set(i, { constantValue: e.target.value })}
              />
            ) : null}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <label className="flex min-h-11 cursor-pointer items-center gap-2 text-[14px]">
                <input
                  type="checkbox"
                  className="size-5"
                  checked={r.required}
                  onChange={(e) => set(i, { required: e.target.checked })}
                />
                Обязательное
              </label>
              <Button size="sm" variant="ghost" onClick={() => setRows((x) => x.filter((_, j) => j !== i))}>
                Убрать строку
              </Button>
            </div>
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          onClick={() =>
            setRows((r) => [
              ...r,
              {
                portalField: '',
                externalField: '',
                transform: 'as_is',
                constantValue: null,
                required: false,
              },
            ])
          }
        >
          Добавить строку
        </Button>
        <Button
          size="sm"
          variant="primary"
          loading={save.isPending}
          disabled={!entity}
          onClick={() => save.mutate()}
        >
          Сохранить сопоставление
        </Button>
      </div>
      {save.error ? <ErrorBox error={save.error} /> : null}
    </div>
  );
}

/** Money-портал (ФН-04): адрес, окно загрузки, проверка связи и загрузка сейчас; ключ вносится выше */
function MoneyPortalSettings({ c }: { c: ConnectorView }) {
  const qc = useQueryClient();
  const [url, setUrl] = useState(String(c.config.url ?? ''));
  const [windowDays, setWindowDays] = useState(String(c.config.windowDays ?? 14));
  const save = useMutation({
    mutationFn: () =>
      api(`/integrations/connectors/${c.id}`, {
        method: 'PATCH',
        body: { config: { url: url.trim(), windowDays: Number(windowDays) || 14 } },
      }),
    onSuccess: () => {
      toast('Настройки сохранены');
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
  const check = useMutation({
    mutationFn: () =>
      api<{ ok: boolean; service: string }>(`/integrations/connectors/${c.id}/money/check`, {
        method: 'POST',
      }),
    onSuccess: () => toast('Связь с money-порталом есть'),
  });
  const sync = useMutation({
    mutationFn: () =>
      api<{ loaded: number; matched: number; unmatched: number; excluded: number }>(
        `/integrations/connectors/${c.id}/money/sync`,
        { method: 'POST' },
      ),
    onSuccess: (r) => {
      toast(`Загружено новых строк: ${r.loaded}, разнесено само: ${r.matched}, ждут вас: ${r.unmatched}`);
      void qc.invalidateQueries({ queryKey: KEY });
      void qc.invalidateQueries({ queryKey: ['procurement', 'money-payments'] });
    },
    onError: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
  return (
    <div className="flex flex-col gap-3 border-t border-line pt-3">
      <Field
        label="Адрес money-портала"
        htmlFor={`mpurl-${c.id}`}
        hint="Например, https://money.infratech.kz — только https и внешний адрес. Ключ выпускается в админ-панели money-портала («Обмен с EPC Pilot AI»)."
      >
        <Input
          className="h-11 md:h-10"
          id={`mpurl-${c.id}`}
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://"
        />
      </Field>
      <Field
        label="За сколько дней забирать платежи"
        htmlFor={`mpwin-${c.id}`}
        hint="Каждую загрузку портал просматривает последние дни; повторы не дублируются."
      >
        <Input
          className="h-11 md:h-10"
          id={`mpwin-${c.id}`}
          inputMode="numeric"
          value={windowDays}
          onChange={(e) => setWindowDays(e.target.value)}
        />
      </Field>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
          Сохранить адрес
        </Button>
        <Button
          size="sm"
          loading={check.isPending}
          disabled={!c.hasSecret || !c.config.url}
          onClick={() => check.mutate()}
        >
          Проверить связь
        </Button>
        <Button
          size="sm"
          loading={sync.isPending}
          disabled={!c.enabled || !c.hasSecret}
          onClick={() => sync.mutate()}
        >
          Загрузить сейчас
        </Button>
      </div>
      <p className="text-[13px] text-ink-2">
        Автоматически платежи загружаются раз в час, когда коннектор включён и в режиме «Автоматически».
        Оплаты видны в «Финансы → Оплаты из банка».
      </p>
      {save.error ? <ErrorBox error={save.error} /> : null}
      {check.error ? <ErrorBox error={check.error} /> : null}
      {sync.error ? <ErrorBox error={sync.error} /> : null}
    </div>
  );
}

function ConnectorCard({ c }: { c: ConnectorView }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [secret, setSecret] = useState('');
  const [entity, setEntity] = useState<ExchangeEntity | ''>(c.entities[0] ?? '');
  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api(`/integrations/connectors/${c.id}`, { method: 'PATCH', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
  const file = useMutation({
    mutationFn: () => downloadCsv(c.id, entity),
    onSuccess: () => {
      toast('Файл выгружен: внесите номера 1С, когда они появятся');
      void qc.invalidateQueries({ queryKey: KEY });
      void qc.invalidateQueries({ queryKey: QKEY });
    },
  });
  const remove = useMutation({
    mutationFn: () => api(`/integrations/connectors/${c.id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
  const waiting = c.counts.pending + c.counts.failed + c.counts.dead + c.counts.fileReady;
  return (
    <li className="flex flex-col gap-3 rounded-md border border-line p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="min-w-0 font-medium [overflow-wrap:anywhere] text-ink">{c.name}</span>
        <Badge tone={c.enabled ? 'success' : 'neutral'}>{c.enabled ? 'включён' : 'выключен'}</Badge>
      </div>
      <div className="flex flex-wrap gap-1.5 text-[13px] text-ink-2">
        <span>{CONNECTOR_KINDS[c.kind as ConnectorKind]}</span>
        <span>· режим: {c.mode === 'file' ? 'только файл' : 'автоматически'}</span>
        {!c.ready ? <Badge tone="warning">заготовка: ждёт согласованного формата</Badge> : null}
        {c.externalChannelsOff ? <Badge>внешние каналы выключены (демо)</Badge> : null}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {c.counts.pending ? <Badge tone="info">ждёт отправки: {c.counts.pending}</Badge> : null}
        {c.counts.failed ? <Badge tone="warning">ошибка, будет повтор: {c.counts.failed}</Badge> : null}
        {c.counts.dead ? <Badge tone="danger">остановлено: {c.counts.dead}</Badge> : null}
        {c.counts.fileReady ? <Badge tone="accent">ждут номеров 1С: {c.counts.fileReady}</Badge> : null}
        {!waiting ? <span className="text-[13px] text-ink-2">Очередь пуста</span> : null}
      </div>
      {c.lastError ? (
        <p className="text-[13px] [overflow-wrap:anywhere] text-danger">Последняя ошибка: {c.lastError}</p>
      ) : null}
      {c.lastSuccessAt ? (
        <p className="text-[13px] text-ink-2">Последняя отправка: {dateTime(c.lastSuccessAt)}</p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant={c.enabled ? 'secondary' : 'primary'}
          loading={patch.isPending}
          onClick={() => patch.mutate({ enabled: !c.enabled })}
        >
          {c.enabled ? 'Выключить' : 'Включить'}
        </Button>
        <Button size="sm" onClick={() => setOpen((v) => !v)}>
          {open ? 'Свернуть настройки' : 'Настройки и сопоставление'}
        </Button>
      </div>
      {patch.error ? <ErrorBox error={patch.error} /> : null}
      {open ? (
        <div className="flex flex-col gap-4 border-t border-line pt-3">
          {c.modes.length > 1 ? (
            <Field label="Режим обмена" htmlFor={`mode-${c.id}`}>
              <Select
                className="h-11 md:h-10"
                id={`mode-${c.id}`}
                value={c.mode}
                onChange={(e) => patch.mutate({ mode: e.target.value })}
              >
                <option value="file">Только файл: выгрузка и номера вносятся вручную</option>
                <option value="live">Автоматически: отправка с повтором</option>
              </Select>
            </Field>
          ) : null}
          <Field
            label="Ключ доступа"
            htmlFor={`sec-${c.id}`}
            hint={
              c.hasSecret
                ? 'Ключ внесён и хранится зашифрованным; показать его нельзя. Новый ключ заменит прежний.'
                : 'Ключ хранится зашифрованным и после сохранения не показывается.'
            }
          >
            <Input
              className="h-11 md:h-10"
              id={`sec-${c.id}`}
              type="password"
              autoComplete="off"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              placeholder={c.hasSecret ? '••••••••' : 'Ключ'}
            />
          </Field>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              disabled={!secret}
              onClick={() => patch.mutate({ secret }, { onSuccess: () => setSecret('') })}
            >
              Сохранить ключ
            </Button>
            {c.hasSecret ? (
              <Button size="sm" variant="danger" onClick={() => patch.mutate({ secret: null })}>
                Стереть ключ
              </Button>
            ) : null}
          </div>
          {c.kind === 'money_portal' ? <MoneyPortalSettings c={c} /> : <MapEditor c={c} />}
          {c.mode === 'file' && c.entities.length > 0 ? (
            <div className="flex flex-col gap-2 border-t border-line pt-3">
              <Field
                label="Выгрузить в файл"
                htmlFor={`exp-${c.id}`}
                hint="В файл попадают записи со статусом «ждёт отправки»; столбцы — по вашему сопоставлению. Первый столбец — ключ обмена, по нему вносятся номера 1С."
              >
                <Select
                  className="h-11 md:h-10"
                  id={`exp-${c.id}`}
                  value={entity}
                  onChange={(e) => setEntity(e.target.value as ExchangeEntity)}
                >
                  {c.entities.map((e) => (
                    <option key={e} value={e}>
                      {EXCHANGE_ENTITIES[e]}
                    </option>
                  ))}
                </Select>
              </Field>
              <div>
                <Button
                  size="sm"
                  variant="primary"
                  loading={file.isPending}
                  disabled={!c.enabled || !entity}
                  onClick={() => file.mutate()}
                >
                  Скачать файл
                </Button>
              </div>
              {file.error ? <ErrorBox error={file.error} /> : null}
            </div>
          ) : null}
          <div>
            <Button
              size="sm"
              variant="danger"
              loading={remove.isPending}
              onClick={() => {
                if (
                  window.confirm(
                    `Удалить коннектор «${c.name}»? Сопоставление и журнал обмена будут удалены.`,
                  )
                )
                  remove.mutate();
              }}
            >
              Удалить коннектор
            </Button>
            {remove.error ? <ErrorBox className="mt-2" error={remove.error} /> : null}
          </div>
        </div>
      ) : null}
    </li>
  );
}

function QueueRow({ q }: { q: QueueItemView }) {
  const qc = useQueryClient();
  const [ref, setRef] = useState('');
  const [showLog, setShowLog] = useState(false);
  const log = useQuery({
    queryKey: ['integrations', 'log', q.id],
    enabled: showLog,
    queryFn: () =>
      api<Array<{ id: string; at: string; ok: boolean; message: string }>>(`/integrations/queue/${q.id}/log`),
  });
  const done = () => {
    void qc.invalidateQueries({ queryKey: QKEY });
    void qc.invalidateQueries({ queryKey: KEY });
  };
  const act = useMutation({
    mutationFn: (a: { path: string; body?: unknown }) =>
      api(`/integrations/queue/${q.id}/${a.path}`, { method: 'POST', body: a.body ?? {} }),
    onSuccess: done,
  });
  const tone =
    q.status === 'dead'
      ? 'danger'
      : q.status === 'failed'
        ? 'warning'
        : q.status === 'confirmed' || q.status === 'sent'
          ? 'success'
          : 'info';
  const open = ['pending', 'failed', 'dead', 'file_ready'].includes(q.status);
  return (
    <li className="flex flex-col gap-2 py-3 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="min-w-0 font-medium [overflow-wrap:anywhere] text-ink">{q.entityTitle}</span>
        <Badge tone={tone}>{q.statusTitle}</Badge>
      </div>
      <div className="text-[13px] text-ink-2">
        {q.connectorName} · создано {dateTime(q.createdAt)} · попыток: {q.attempts}
        {q.nextAttemptAt && q.status === 'failed' ? ` · следующая ${dateTime(q.nextAttemptAt)}` : ''}
        {q.externalRef ? ` · номер: ${q.externalRef}` : ''}
      </div>
      {q.missingFields.length ? (
        <div className="text-[13px] text-warning">Нет обязательных полей: {q.missingFields.join(', ')}</div>
      ) : null}
      {q.lastError ? (
        <div className="text-[13px] [overflow-wrap:anywhere] text-danger">{q.lastError}</div>
      ) : null}
      {q.status === 'file_ready' ? (
        <div className="flex flex-wrap gap-2">
          <Input
            aria-label="Номер в 1С"
            placeholder="Номер в 1С"
            value={ref}
            onChange={(e) => setRef(e.target.value)}
            className="h-11 max-w-xs md:h-10"
          />
          <Button
            size="sm"
            variant="primary"
            disabled={!ref.trim()}
            loading={act.isPending}
            onClick={() => act.mutate({ path: 'confirm', body: { externalRef: ref.trim() } })}
          >
            Внести номер
          </Button>
        </div>
      ) : null}
      <div className="flex flex-wrap gap-2">
        {['failed', 'dead', 'pending'].includes(q.status) ? (
          <Button size="sm" loading={act.isPending} onClick={() => act.mutate({ path: 'retry' })}>
            Повторить сейчас
          </Button>
        ) : null}
        {open ? (
          <Button
            size="sm"
            variant="danger"
            onClick={() =>
              window.confirm('Снять запись с обмена? Во внешнюю систему она не уйдёт.') &&
              act.mutate({ path: 'cancel' })
            }
          >
            Снять с обмена
          </Button>
        ) : null}
        <Button size="sm" variant="ghost" onClick={() => setShowLog((v) => !v)}>
          {showLog ? 'Скрыть журнал' : 'Журнал попыток'}
        </Button>
      </div>
      {act.error ? <ErrorBox error={act.error} /> : null}
      {showLog ? (
        log.isPending ? (
          <Spinner />
        ) : (
          <ul className="flex flex-col gap-1 text-[13px]">
            {log.data?.map((l) => (
              <li key={l.id} className={l.ok ? 'text-ink-2' : 'text-danger'}>
                {dateTime(l.at)} — {l.message}
              </li>
            ))}
          </ul>
        )
      ) : null}
    </li>
  );
}

/**
 * Обмен с внешними системами (раздел 7 ТЗ, ИН-01–05): коннекторы, сопоставление полей, очередь и журнал.
 * Формат обмена с 1С задаёт администратор сопоставлением полей; пока он не согласован, работает режим «только файл».
 */
export function IntegrationsPage() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: KEY, queryFn: () => api<ConnectorView[]>('/integrations/connectors') });
  const queueQ = useQuery({
    queryKey: QKEY,
    queryFn: () => api<QueueItemView[]>('/integrations/queue', { query: { limit: 50 } }),
    refetchInterval: 30_000,
  });
  const [kind, setKind] = useState<ConnectorKind>('onec_file');
  const [name, setName] = useState('');
  const create = useMutation({
    mutationFn: () => api('/integrations/connectors', { method: 'POST', body: { kind, name } }),
    onSuccess: () => {
      setName('');
      toast('Коннектор добавлен: он выключен, пока вы его не включите');
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
  return (
    <div className="flex flex-col gap-6">
      <Panel title="Коннекторы" meta="Внешние системы, с которыми обменивается портал">
        {list.isPending ? (
          <Spinner />
        ) : list.error ? (
          <ErrorBox error={list.error} />
        ) : !list.data!.length ? (
          <EmptyState title="Коннекторов пока нет">
            Добавьте коннектор ниже: для 1С на первом этапе — «обмен файлами».
          </EmptyState>
        ) : (
          <ul className="flex flex-col gap-3">
            {list.data!.map((c) => (
              <ConnectorCard key={c.id} c={c} />
            ))}
          </ul>
        )}
      </Panel>

      <Panel title="Добавить коннектор">
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <Field label="Вид" htmlFor="new-kind">
            <Select
              className="h-11 md:h-10"
              id="new-kind"
              value={kind}
              onChange={(e) => setKind(e.target.value as ConnectorKind)}
            >
              {Object.entries(CONNECTOR_KINDS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Название" htmlFor="new-name">
            <Input
              className="h-11 md:h-10"
              id="new-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Например, 1С бухгалтерия"
            />
          </Field>
          <div>
            <Button
              type="submit"
              variant="primary"
              loading={create.isPending}
              disabled={name.trim().length < 2}
            >
              Добавить
            </Button>
          </div>
          {create.error ? <ErrorBox error={create.error} /> : null}
        </form>
      </Panel>

      <Panel title="Очередь обмена" meta="Последние 50 записей">
        {queueQ.isPending ? (
          <Spinner />
        ) : queueQ.error ? (
          <ErrorBox error={queueQ.error} />
        ) : !queueQ.data!.length ? (
          <EmptyState title="В очереди пусто">
            Записи появляются, когда разделы портала передают данные на выгрузку.
          </EmptyState>
        ) : (
          <ul className="flex flex-col divide-y divide-line">
            {queueQ.data!.map((q) => (
              <QueueRow key={q.id} q={q} />
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}
